import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { BoardSummaryFetcher } from 'app/trello/services/board-summary-fetcher';
import { ComparableDateRangeProvider } from 'app/trello/services/comparable-date-range-provider';
import { LabelsFetcher } from 'app/trello/services/labels-fetcher';
import { ListsFetcher } from 'app/trello/services/lists-fetcher';
import { MembersFetcher } from 'app/trello/services/members-fetcher';
import isEqual from 'lodash-es/isEqual';
import { catchError, distinctUntilChanged, filter, map, mergeMap, of, switchMap, take } from 'rxjs';
import {
  boardSummaryReportDateRangeSelected,
  boardSummaryReportOpened,
  loadBoardSummaryStatistics,
  loadBoardSummaryStatisticsFailure,
  loadBoardSummaryStatisticsSuccess,
  loadComparableBoardSummaryStatistics,
  loadComparableBoardSummaryStatisticsFailure,
  loadComparableBoardSummaryStatisticsSuccess,
  loadLabels,
  loadLabelsFailure,
  loadLabelsSuccess,
  loadLists,
  loadListsFailure,
  loadListsSuccess,
  loadMembers,
  loadMembersFailure,
  loadMembersSuccess
} from './board-summary-report.actions';

@Injectable()
export class BoardSummaryReportEffects {

  // ------------------------------------------------------

  boardSummaryReportOpened$ = createEffect(() =>
    this.actions$.pipe(
      ofType(boardSummaryReportOpened),
      mergeMap((action) => [
        loadLabels({now: action.now}),
        loadLists({now: action.now}),
        loadMembers({now: action.now}),
      ]),
    ),
  );

  boardSummaryReportDateRangeSelected$ = createEffect(() =>
    this.actions$.pipe(
      ofType(boardSummaryReportDateRangeSelected),
      mergeMap((action) => [
        loadBoardSummaryStatistics({dateRange: action.dateRange, now: action.now}),
        loadLabels({now: action.now}),
        loadLists({now: action.now}),
        loadMembers({now: action.now}),
      ]),
    ),
  );

  // ------------------------------------------------------

  loadBoardSummaryStatistics$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadBoardSummaryStatistics),
      distinctUntilChanged((prev, curr) => isEqual(prev, curr)),
      switchMap(action => {

        const from = action.dateRange.rangeFrom!;
        const to = action.dateRange.rangeTo!;
        const now = action.now;

        return this.boardSummaryStatisticsFetcher.fetch(from, to, now)
          .pipe(
            map(boardSummary => loadBoardSummaryStatisticsSuccess({boardSummary, dateRange: action.dateRange, now: action.now})),
            catchError(error => of(loadBoardSummaryStatisticsFailure({
              error: error instanceof Error ? error.message : 'Unknown error loading board summary statistics',
            }))),
          );
      }),
    ),
  );

  // ------------------------------------------------------

  loadComparableBoardSummaryStatisticsAfterMain$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadBoardSummaryStatisticsSuccess),
      switchMap(action =>
        this.comparableDateRangeProvider.getComparableDateRange(action.dateRange)
          .pipe(
            take(1),
            filter(comparableDateRange => !!comparableDateRange),
            map(comparableDateRange =>
              loadComparableBoardSummaryStatistics({
                comparableDateRange: comparableDateRange!,
                now: action.now,
              }),
            ),
          ),
      ),
    ),
  );

  loadComparableBoardSummaryStatistics$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadComparableBoardSummaryStatistics),
      distinctUntilChanged((prev, curr) => isEqual(prev, curr)),
      switchMap(action => {

        const from = action.comparableDateRange.rangeFrom!;
        const to = action.comparableDateRange.rangeTo!;
        const now = action.now;

        return this.boardSummaryStatisticsFetcher.fetch(from, to, now)
          .pipe(
            map(comparableBoardSummary => loadComparableBoardSummaryStatisticsSuccess({comparableBoardSummary, comparableDateRange: action.comparableDateRange, now: action.now})),
            catchError(error => of(loadComparableBoardSummaryStatisticsFailure({
              error: error instanceof Error ? error.message : 'Unknown error loading comparable board summary statistics',
            }))),
          );
      }),
    ),
  );

  // ------------------------------------------------------

  loadLabels$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadLabels),
      distinctUntilChanged((prev, curr) => isEqual(prev, curr)),
      switchMap((action) => {

        const now = action.now;

        return this.labelsFetcher.fetch(now)
          .pipe(
            map(labels => loadLabelsSuccess({labels: labels.labels || [], now})),
            catchError(error => of(loadLabelsFailure({
              error: error instanceof Error ? error.message : 'Unknown error loading labels',
            }))),
          );
      }),
    ),
  );

  // ------------------------------------------------------

  loadLists$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadLists),
      distinctUntilChanged((prev, curr) => isEqual(prev, curr)),
      switchMap((action) => {

        const now = action.now;

        return this.listsFetcher.fetch(now)
          .pipe(
            map(lists => loadListsSuccess({lists: lists.lists || [], now})),
            catchError(error => of(loadListsFailure({
              error: error instanceof Error ? error.message : 'Unknown error loading lists',
            }))),
          );
      }),
    ),
  );

  // ------------------------------------------------------

  loadMembers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadMembers),
      distinctUntilChanged((prev, curr) => isEqual(prev, curr)),
      switchMap((action) => {

        const now = action.now;

        return this.membersFetcher.fetch(now)
          .pipe(
            map(members => loadMembersSuccess({members: members.members || [], now})),
            catchError(error => of(loadMembersFailure({
              error: error instanceof Error ? error.message : 'Unknown error loading members',
            }))),
          );
      }),
    ),
  );

  // ------------------------------------------------------

  constructor(
    private actions$: Actions,
    private boardSummaryStatisticsFetcher: BoardSummaryFetcher,
    private comparableDateRangeProvider: ComparableDateRangeProvider,
    private labelsFetcher: LabelsFetcher,
    private listsFetcher: ListsFetcher,
    private membersFetcher: MembersFetcher
  ) {
  }

}
